import { observable } from 'mobx'

import { utils } from '@maxus/common'
import { isTerminalWorkflowStatus, type ReviewStage } from '@maxus/common/domain'
import { bridge, ResolveBridgeError, trpcClient } from '@maxus/website/lib'

import { executeFrontendTaskRetry, getPendingFrontendTaskRetry } from './frontend-task-retry'

// 浏览器端后台渲染管理器（镜像 uploadManager）：负责驱动本机 resolve bridge 渲染，并从 bridge
// 轮询进度回写到 server task，不依赖创建面板是否还开着。进度经 server task 落库，
// 其余用户也能通过 progressStore 轮询 workflow.list 看到。
// register() 只等到 createRender 建好 workflow；实际渲染（setup→start→poll→finish）在后台进行。

type RegisterRenderInput = {
  deliverableId: string
  stageName: ReviewStage
  platformNames: string[]
  timelineName: string
  timelineVersionId?: string
  note?: string
}

type RenderSetup = Omit<
  Awaited<ReturnType<typeof trpcClient.project.asset.createRender.mutate>>[number],
  'platformName' | 'workflowId'
> & { workflowId: string }
type RenderResult = Awaited<ReturnType<typeof trpcClient.project.asset.finishRender.mutate>>
type PostRenderContext = { isCanceled: () => boolean }
type PostRenderResult<T> = { status: 'canceled' } | { status: 'completed'; value: T }
type RenderManagerEvent = { type: 'bridge-failed'; error: ResolveBridgeError }
type RenderManagerListener = (event: RenderManagerEvent) => void

export class RenderCanceledError extends Error {
  constructor() {
    super('render was canceled')
    this.name = 'RenderCanceledError'
  }
}

const POLL_INTERVAL_MS = 3_000
const MAX_POLL_ATTEMPTS = 600 // ≈30 分钟

// DaVinci JobStatus 字面量（透传自 Resolve，含中英文）。
const DONE_STATUSES = new Set(['Complete', '完成'])
const FAILED_STATUSES = new Set([
  'Failed',
  'Cancelled',
  'Canceled',
  '失败',
  '已取消',
  '取消',
])

const CURRENT_CLIENT_WORKFLOW_IDS_KEY = 'maxus_render_workflow_ids'

type CachedRenderWorkflow = {
  attempt?: number
  workflowId: string
  renderJobId?: string
  taskId?: string
  terminal?: true
}

type CachedRenderJob = Required<Pick<CachedRenderWorkflow, 'renderJobId' | 'taskId' | 'workflowId'>> &
  Pick<CachedRenderWorkflow, 'attempt'>

type CachedRenderWorkflows = {
  records: CachedRenderWorkflow[]
  version: 2
}

const parseCachedRenderWorkflow = (value: unknown): CachedRenderWorkflow | undefined => {
  if (typeof value === 'string') return { workflowId: value }
  if (typeof value !== 'object' || value === null) return

  const record = value as Record<string, unknown>
  if (typeof record.workflowId !== 'string') return
  return {
    workflowId: record.workflowId,
    ...(typeof record.renderJobId === 'string' ? { renderJobId: record.renderJobId } : {}),
    ...(typeof record.taskId === 'string' ? { taskId: record.taskId } : {}),
    ...(typeof record.attempt === 'number' ? { attempt: record.attempt } : {}),
    ...(record.terminal === true
      ? { terminal: true as const }
      : {}),
  }
}

const readCachedRenderWorkflows = (): CachedRenderWorkflow[] => {
  try {
    const cached: unknown = JSON.parse(localStorage.getItem(CURRENT_CLIENT_WORKFLOW_IDS_KEY) ?? '[]')
    const values = Array.isArray(cached)
      ? cached
      : typeof cached === 'object' && cached !== null && Array.isArray((cached as CachedRenderWorkflows).records)
        ? (cached as CachedRenderWorkflows).records
        : []
    return values.flatMap(value => {
      const record = parseCachedRenderWorkflow(value)
      return record ? [record] : []
    })
  } catch {
    return []
  }
}

const writeCachedRenderWorkflows = (records: CachedRenderWorkflow[]) => {
  const cached: CachedRenderWorkflows = { records, version: 2 }
  try {
    localStorage.setItem(CURRENT_CLIENT_WORKFLOW_IDS_KEY, JSON.stringify(cached))
  } catch {
    // localStorage 不可用（隐私浏览、存储已满等）。
  }
}

const cacheCurrentClientWorkflowIds = (workflowIds: string[]) => {
  const records = readCachedRenderWorkflows()
  const cachedIds = new Set(records.map(({ workflowId }) => workflowId))
  for (const workflowId of workflowIds) {
    if (!cachedIds.has(workflowId)) {
      records.push({ workflowId })
      cachedIds.add(workflowId)
    }
  }
  writeCachedRenderWorkflows(records)
}

const cacheRenderJob = (record: CachedRenderJob) => {
  const records = readCachedRenderWorkflows()
  const index = records.findIndex(({ workflowId }) => workflowId === record.workflowId)
  if (index === -1) records.push(record)
  else records[index] = record
  writeCachedRenderWorkflows(records)
}

const clearCachedRenderJob = (workflowId: string) => {
  const records = readCachedRenderWorkflows()
  const index = records.findIndex(record => record.workflowId === workflowId)
  if (index === -1) return
  records[index] = { workflowId }
  writeCachedRenderWorkflows(records)
}

const markCachedWorkflowTerminal = (workflowId: string) => {
  const records = readCachedRenderWorkflows()
  const index = records.findIndex(record => record.workflowId === workflowId)
  if (index === -1) return
  records[index] = { terminal: true, workflowId }
  writeCachedRenderWorkflows(records)
}

export class RenderManager {
  private initializePromise: Promise<void> | null = null
  private listeners = new Set<RenderManagerListener>()
  private retryPromises = new Map<string, Promise<void>>()
  // 当前正在本机渲染的 workflowId（达芬奇一次只渲一个）。
  private activeRenderWorkflowId = observable.box<string | null>(null)
  // setupRenderJob 返回后记录当前 Resolve job；取消只在确有对应 job 时调用 bridge stop。
  private activeRenderJobId: string | null = null
  // 主动 stop 的 workflowId：其失败不回写 failed（由面板置 canceled）。
  private stopping = new Set<string>()
  // 所有 Resolve render 共用同一条队列，避免普通 render 与母带 render 并发操作 DaVinci。
  private renderQueue: Promise<void> = Promise.resolve()
  private queuedWorkflowIds = observable.set<string>()

  async register({
    deliverableId,
    stageName,
    platformNames,
    timelineName,
    timelineVersionId,
    note,
  }: RegisterRenderInput) {
    await this.initialize()
    // 前置健康检查：bridge 不可达 / Resolve 未就绪时直接抛错，避免建了 workflow 却无法渲染。
    const health = await bridge.getHealth()
    if (health.resolve_status.status !== 'ready') {
      throw new ResolveBridgeError('unhealthy', `DaVinci Resolve is not ready: ${health.resolve_status.status}`)
    }

    const setups = await trpcClient.project.asset.createRender.mutate({
      deliverableId,
      stageName,
      platformNames,
      timelineName,
      timelineVersionId,
      note,
    })
    cacheCurrentClientWorkflowIds(setups.map(({ workflowId }) => workflowId))

    // DaVinci 一次只渲染一个作业：各 platform 串行 setup→start→poll→finish；后台执行，不阻塞面板关闭。
    for (const setup of setups) this.queuedWorkflowIds.add(setup.workflowId)
    void this
      .enqueue(() => this.runAll(setups))
      .finally(() => {
        for (const setup of setups) this.queuedWorkflowIds.delete(setup.workflowId)
      })
  }

  private async runAll(setups: RenderSetup[]) {
    let bridgeErrorReported = false
    for (const setup of setups) {
      try {
        await this.renderOne(setup)
      } catch (error) {
        if (error instanceof ResolveBridgeError && !bridgeErrorReported) {
          bridgeErrorReported = true
          this.emit({ type: 'bridge-failed', error })
        }
      }
    }
  }

  // 已由其他业务入口创建好的 render workflow 可把后续 Resolve 操作一并放进队列；
  // workflow 被取消后，postRender 通过 isCanceled 在步骤边界停止。
  async runWithPostRender<T>(
    setup: RenderSetup,
    postRender: (result: RenderResult, context: PostRenderContext) => Promise<T>,
  ): Promise<PostRenderResult<Awaited<T>>> {
    await this.initialize()
    cacheCurrentClientWorkflowIds([setup.workflowId])
    this.queuedWorkflowIds.add(setup.workflowId)
    return this
      .enqueue(async () => {
        try {
          const result = await this.renderOne(setup)
          if (this.stopping.has(setup.workflowId)) {
            return { status: 'canceled' } as const
          }
          const value = await postRender(result, {
            isCanceled: () => this.stopping.has(setup.workflowId),
          })
          return { status: 'completed', value } as const
        } catch (error) {
          if (error instanceof RenderCanceledError) {
            return { status: 'canceled' } as const
          }
          throw error
        }
      })
      .finally(() => {
        this.queuedWorkflowIds.delete(setup.workflowId)
        this.stopping.delete(setup.workflowId)
      })
  }

  // 非 render 的 Resolve 写操作（如复制时间线）也必须与渲染共用同一队列。
  async runExclusive<T>(run: () => Promise<T>): Promise<T> {
    await this.initialize()
    return this.enqueue(run)
  }

  initialize() {
    this.initializePromise ??= this.restoreCachedRenders()
    return this.initializePromise
  }

  subscribe(listener: RenderManagerListener) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(event: RenderManagerEvent) {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (error) {
        console.error('[render-manager] listener failed', error)
      }
    }
  }

  private enqueue<T>(run: () => Promise<T>): Promise<T> {
    const result = this.renderQueue.then(run, run)
    this.renderQueue = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }

  private async restoreCachedRenders() {
    const resumptions: Promise<void>[] = []
    for (const cached of readCachedRenderWorkflows()) {
      if (cached.terminal) continue
      try {
        const workflow = await trpcClient.project.workflow.get.query({ workflowId: cached.workflowId })
        if (isTerminalWorkflowStatus(workflow.status)) {
          markCachedWorkflowTerminal(cached.workflowId)
          continue
        }
      } catch (error) {
        console.error('[render-manager] failed to restore cached workflow', error)
        continue
      }

      if (!cached.renderJobId || !cached.taskId) continue
      this.queuedWorkflowIds.add(cached.workflowId)
      resumptions.push(
        this
          .enqueue(() => this.resumeCachedRender(cached as CachedRenderJob))
          .finally(() => {
            this.queuedWorkflowIds.delete(cached.workflowId)
          }),
      )
    }
    await Promise.allSettled(resumptions)
  }

  private async resumeCachedRender({
    attempt,
    renderJobId,
    taskId,
    workflowId,
  }: CachedRenderJob) {
    const currentAttempt = attempt ?? 1
    if (this.stopping.delete(workflowId)) throw new RenderCanceledError()
    this.activeRenderWorkflowId.set(workflowId)
    this.activeRenderJobId = renderJobId

    try {
      try {
        await this.pollUntilDone(renderJobId, taskId, currentAttempt)
      } catch (error) {
        if (this.stopping.delete(workflowId)) {
          clearCachedRenderJob(workflowId)
          throw new RenderCanceledError()
        }
        if (error instanceof ResolveBridgeError) {
          console.error('[render-manager] failed to resume render polling', error)
          this.emit({ type: 'bridge-failed', error })
          return
        }

        await bridge
          .stopRendering()
          .catch(() => undefined)
        await trpcClient.project.task.update.mutate({
          taskId,
          attempt: currentAttempt,
          status: 'retry-waiting',
          error: error instanceof Error ? error.message : String(error),
        })
        clearCachedRenderJob(workflowId)
        return
      }

      try {
        await trpcClient.project.asset.finishRender.mutate({ taskId, attempt: currentAttempt })
        clearCachedRenderJob(workflowId)
      } catch (error) {
        console.error('[render-manager] failed to finish resumed render', error)
      }
    } finally {
      this.stopping.delete(workflowId)
      if (this.activeRenderWorkflowId.get() === workflowId) {
        this.activeRenderWorkflowId.set(null)
        this.activeRenderJobId = null
      }
    }
  }

  private async renderOne(setup: RenderSetup) {
    // 排队期间被取消的 workflow 不再进入 Resolve。
    if (this.stopping.delete(setup.workflowId)) throw new RenderCanceledError()
    this.activeRenderWorkflowId.set(setup.workflowId)
    try {
      const renderJobId = await bridge.setupRenderJob({
        project_name: setup.projectName,
        project_manager_path: setup.projectManagerPath,
        timeline_name: setup.timelineName,
        render_profile: {
          resolve_preset_url: setup.presetUrl,
          resolve_preset_name: setup.presetName,
          render_setting: {
            render_file_name: setup.renderFileName,
            render_path: setup.renderPath,
            width: setup.width,
            height: setup.height,
            ...(setup.frameRate != null ? { frame_rate: setup.frameRate } : {}),
            ...(setup.fileSizeLimit != null ? { file_size_limit: setup.fileSizeLimit } : {}),
            ...(setup.bitrateLimit != null
              ? { bitrate_limit: setup.bitrateLimit }
              : {}),
            burn_in_subtitle: setup.burnInSubtitle,
          },
        },
      })
      this.activeRenderJobId = renderJobId
      cacheRenderJob({
        attempt: setup.attempt,
        renderJobId,
        taskId: setup.taskId,
        workflowId: setup.workflowId,
      })

      // setup 期间可能已经收到取消；不要再启动这个 job。
      if (this.stopping.has(setup.workflowId)) {
        await bridge
          .stopRendering()
          .catch(() => undefined)
        throw new RenderCanceledError()
      }

      await bridge.startRendering(renderJobId)
      // start 请求与取消可能并发；start 返回后再停一次，保证不会留下刚启动的渲染。
      if (this.stopping.has(setup.workflowId)) {
        await bridge
          .stopRendering()
          .catch(() => undefined)
        throw new RenderCanceledError()
      }
      await this.pollUntilDone(renderJobId, setup.taskId, setup.attempt)
      const result = await trpcClient.project.asset.finishRender.mutate({
        taskId: setup.taskId,
        attempt: setup.attempt,
      })
      clearCachedRenderJob(setup.workflowId)
      if (this.stopping.has(setup.workflowId)) throw new RenderCanceledError()
      return result
    } catch (error) {
      if (error instanceof RenderCanceledError) throw error
      // 主动停止（stop）导致的失败不回写 failed：由面板置 canceled。
      if (this.stopping.delete(setup.workflowId)) throw new RenderCanceledError()
      if (this.activeRenderJobId) {
        await bridge
          .stopRendering()
          .catch(stopError => {
            console.error('[render-manager] failed to stop failed render', stopError)
          })
      }
      // 失败落到 server task，供 progressStore 展示；再抛出交给 runAll 隔离（不影响其他 platform）。
      await trpcClient.project.task.update
        .mutate({
          taskId: setup.taskId,
          attempt: setup.attempt,
          status: 'retry-waiting',
          error: error instanceof Error ? error.message : String(error),
        })
        .catch(reportError => {
          console.error('[render-manager] failed to report task error', reportError)
        })
      throw error
    } finally {
      this.stopping.delete(setup.workflowId)
      if (this.activeRenderWorkflowId.get() === setup.workflowId) {
        this.activeRenderWorkflowId.set(null)
        this.activeRenderJobId = null
      }
    }
  }

  private async pollUntilDone(renderJobId: string, taskId: string, attempt: number) {
    let lastProgress = -1
    for (let pollAttempt = 0; pollAttempt < MAX_POLL_ATTEMPTS; pollAttempt++) {
      const status = await bridge.getRenderStatus(renderJobId)
      const percent = typeof status.CompletionPercentage === 'number' ? status.CompletionPercentage : undefined
      const jobStatus = typeof status.JobStatus === 'string' ? status.JobStatus : undefined

      if (percent != null && percent > lastProgress && percent < 100) {
        lastProgress = percent
        await trpcClient.project.task.update
          .mutate({
            taskId,
            attempt,
            status: 'processing',
            progress: Math.round(percent),
          })
          .catch(() => undefined)
      }

      if (jobStatus && FAILED_STATUSES.has(jobStatus)) throw new Error(`render ${jobStatus}: ${taskId}`)
      if ((percent != null && percent >= 100) || (jobStatus && DONE_STATUSES.has(jobStatus))) return

      await utils.sleep(POLL_INTERVAL_MS)
    }
    throw new Error(`render timed out: ${taskId}`)
  }

  isWorkflowTriggeredByCurrentClient(workflowId: string) {
    return readCachedRenderWorkflows().some(record => record.workflowId === workflowId)
  }

  isQueued(workflowId: string) {
    return this.queuedWorkflowIds.has(workflowId) && this.activeRenderWorkflowId.get() !== workflowId
  }

  getPendingRetry(workflowId: string) {
    return getPendingFrontendTaskRetry(workflowId, 'render')
  }

  retry(workflowId: string, taskId: string, expectedAttempt: number): Promise<void> {
    const existing = this.retryPromises.get(workflowId)
    if (existing) return existing

    const promise = this.retryOnce(workflowId, taskId, expectedAttempt)
    this.retryPromises.set(workflowId, promise)
    const clear = () => {
      if (this.retryPromises.get(workflowId) === promise) this.retryPromises.delete(workflowId)
    }
    void promise.then(clear, clear)
    return promise
  }

  private async retryOnce(workflowId: string, taskId: string, expectedAttempt: number) {
    await this.initialize()
    const health = await bridge.getHealth()
    if (health.resolve_status.status !== 'ready') {
      throw new ResolveBridgeError('unhealthy', `DaVinci Resolve is not ready: ${health.resolve_status.status}`)
    }
    const result = await executeFrontendTaskRetry({
      workflowId,
      taskId,
      expectedAttempt,
      taskType: 'render',
    })
    if (result.taskType !== 'render' || !result.setup) {
      throw new Error('Render retry did not return setup')
    }
    const setup = { ...result.setup, workflowId }
    this.queuedWorkflowIds.add(workflowId)
    void this
      .enqueue(() => this.renderOne(setup))
      .catch(error => {
        console.error('[render-manager] retry failed', error)
        if (error instanceof ResolveBridgeError) this.emit({ type: 'bridge-failed', error })
      })
      .finally(() => {
        this.queuedWorkflowIds.delete(workflowId)
      })
  }

  // 尽力中止：排队中的 workflow 会被跳过；正在渲染的 workflow 同时通知 Resolve 停止。
  async stop(workflowId: string) {
    if (!this.queuedWorkflowIds.has(workflowId) && this.activeRenderWorkflowId.get() !== workflowId) return
    this.stopping.add(workflowId)
    if (this.activeRenderWorkflowId.get() === workflowId && this.activeRenderJobId) {
      await bridge.stopRendering()
    }
  }
}

export const renderManager = new RenderManager()
